import { proxyCustomElement, HTMLElement, createEvent, h, Host } from '@stencil/core/internal/client';
import { c as cl } from './clsx.m.js';
import { c as createTypeValidator, a as createLogger, l as logDeprecatedEventOnce, e as emitEvent } from './utils.js';

// Constants
const component = 'cl-dropzone';
const dropzoneConstants = {
  POSITION: {
    LEFT: 'left',
    CENTER: 'center',
  },
};
// Type-validators
const dropzoneValidator = {
  position: createTypeValidator(dropzoneConstants.POSITION),
};

const clDropzoneCss = ".sc-cl-dropzone-h{display:inline-block}.sc-cl-dropzone-h .sc-cl-dropzone-s>*{color:rgba(var(--color-neutral-900));display:inline}.sc-cl-dropzone-h .sc-cl-dropzone-s>cl-button{display:inherit;margin:0;position:relative}.dropzone--full-width.sc-cl-dropzone-h{position:absolute;width:100%}.dropzone--full-height.sc-cl-dropzone-h{height:100%;position:absolute}.dropzone--full-height.sc-cl-dropzone-h .dropzone__container.sc-cl-dropzone{max-height:none}.dropzone--full-height.sc-cl-dropzone-h .dropzone__container__slots.sc-cl-dropzone{width:-moz-fit-content;width:fit-content}.dropzone--hidden.sc-cl-dropzone-h .dropzone__container.sc-cl-dropzone{opacity:0}.dropzone--hidden.sc-cl-dropzone-h .dropzone__container.sc-cl-dropzone .dropzone__slots.sc-cl-dropzone{opacity:0}.dropzone--hidden.sc-cl-dropzone-h .dropzone__container--dragging.sc-cl-dropzone{opacity:1}.dropzone--hidden.sc-cl-dropzone-h .dropzone__container--dragging.sc-cl-dropzone .dropzone__slots.sc-cl-dropzone{opacity:1}.dropzone--left.sc-cl-dropzone-h .dropzone__container.sc-cl-dropzone{justify-content:left;padding:var(--spacing-2)}.dropzone--left.sc-cl-dropzone-h .dropzone__slots.sc-cl-dropzone{justify-content:left}.dropzone--center.sc-cl-dropzone-h .dropzone__container.sc-cl-dropzone{justify-content:center;padding:var(--spacing-2)}.dropzone--center.sc-cl-dropzone-h .dropzone__slots.sc-cl-dropzone{justify-content:center}.sc-cl-dropzone-h .dropzone__slots.sc-cl-dropzone{align-items:center;box-sizing:border-box;display:flex;width:100%}.sc-cl-dropzone-h .dropzone__container.sc-cl-dropzone{align-items:center;border-color:rgba(var(--color-brand-main));border-radius:var(--border-radius-interactive-small);border-style:dashed;border-width:var(--border-width);box-sizing:border-box;color:rgba(var(--color-neutral-900));display:flex;height:100%;justify-content:center;max-height:var(--spacing-8);padding:var(--spacing-2);width:100%}.sc-cl-dropzone-h .dropzone__container--dragging.sc-cl-dropzone{background-color:rgba(var(--color-brand-100));cursor:pointer}.sc-cl-dropzone-h .dropzone__container--dragging.sc-cl-dropzone *.sc-cl-dropzone{pointer-events:none}.sc-cl-dropzone-h .dropzone__container--pictogram.sc-cl-dropzone{max-height:none;padding:var(--spacing-3)}.sc-cl-dropzone-h .dropzone__container--pictogram.sc-cl-dropzone .dropzone__slots.sc-cl-dropzone{display:block;width:auto}.sc-cl-dropzone-h .dropzone__container--pictogram .sc-cl-dropzone-s>p{display:block;margin:0 auto;margin-top:var(--spacing-2);width:-moz-fit-content;width:fit-content}.sc-cl-dropzone-h .dropzone__container--pictogram .sc-cl-dropzone-s>cl-button{display:block;margin:0 auto;margin-top:var(--spacing-4)}.sc-cl-dropzone-h .dropzone__container--pictogram .sc-cl-dropzone-s>cl-pictogram{display:block;margin:0 auto var(--spacing-2)}.sc-cl-dropzone-h .dropzone__container--paragraph-only.sc-cl-dropzone{padding:var(--spacing-3)}.sc-cl-dropzone-h .dropzone__container--paragraph-only.sc-cl-dropzone .dropzone__slots.sc-cl-dropzone{display:flex;width:auto}.sc-cl-dropzone-h .dropzone__container--paragraph-only .sc-cl-dropzone-s>p{display:block;width:-moz-fit-content;width:fit-content}.sc-cl-dropzone-h .dropzone__container--paragraph-only .sc-cl-dropzone-s>cl-button{display:block}.sc-cl-dropzone-h .dropzone__container--paragraph .sc-cl-dropzone-s>p{margin:0;padding-top:var(--spacing-3)}";

const ClDropzone$1 = /*@__PURE__*/ proxyCustomElement(class ClDropzone extends HTMLElement {
  constructor() {
    super();
    this.__registerHost();
    this.clDrop = createEvent(this, "clDrop", 7);
    this.drop = createEvent(this, "drop", 7);
    /**
     * Object containing custom consoleLogger functions.
     */
    this.logger = createLogger(component);
    /**
     * Handles setting the `dragging` state to true when the dragged element enters the dropzone.
     */
    this.handleDragEnter = (event) => {
      event.preventDefault();
      this.dragging = true;
    };
    /**
     * Handles preventing the default action from being executed.
     * This is necessary to do because otherwise the `onDrop` function breaks
     */
    this.handleDragOver = (event) => {
      event.preventDefault();
    };
    /**
     * Handles setting the `dragging` state to false when the dragged element leaves the dropzone.
     */
    this.handleDragLeave = (event) => {
      event.preventDefault();
      this.dragging = false;
    };
    /**
     * Handles binding the private `handleDrop` function to the scope of this.element
     */
    this.bindHandleDrop = this.handleDrop.bind(this);
    this.dragging = false;
    this.spacingType = 'paragraph';
    this.position = 'center';
    this.fullWidth = false;
    this.fullHeight = false;
    this.hidden = false;
  }
  /**
   * Validates the position property
   */
  handlePosition(position) {
    if (!dropzoneValidator.position(position)) {
      this.logger.error.attribute.position(`Given invalid value "${position}".`);
    }
  }
  /**
   * Every time the component is connected to the DOM:
   * - Console log deprecation warnings;
   * - Validate the position property;
   */
  connectedCallback() {
    logDeprecatedEventOnce(this.logger, 'drop', 'clDrop');
    this.handlePosition(this.position);
  }
  /**
   * Based on what the user puts in the slot, the component adds different classes to those elements.
   * If the user adds a button, there will be an input element created.
   */
  componentDidRender() {
    if (this.slots) {
      const childNodes = Array.from(this.slots.childNodes).map(({ nodeName }) => nodeName);
      switch (true) {
        case childNodes.includes('CL-PICTOGRAM'):
          this.spacingType = 'pictogram';
          break;
        case childNodes.includes('CL-BUTTON'):
          this.spacingType = 'button';
          break;
        case childNodes.includes('CL-PARAGRAPH'):
          this.spacingType = 'paragraph';
          break;
      }
    }
  }
  /**
   * Handles emitting the File data when the file is dropped on the dropzone
   * and sets the `dragging` state to false.
   */
  handleDrop(event) {
    var _a, _b;
    event.preventDefault();
    if (!event.detail) {
      event.stopPropagation();
    }
    const droppedFile = (_b = (_a = event === null || event === void 0 ? void 0 : event.dataTransfer) === null || _a === void 0 ? void 0 : _a.files) === null || _b === void 0 ? void 0 : _b[0];
    if (droppedFile) {
      emitEvent(this, 'clDrop', { component, originalEvent: event, value: droppedFile }, droppedFile);
    }
    this.dragging = false;
  }
  render() {
    const { dragging, fullHeight, fullWidth, hidden, position } = this;
    return (h(Host, { class: cl(`dropzone
           dropzone--${position}`, {
        'dropzone--hidden': hidden,
        'dropzone--full-height': fullHeight,
        'dropzone--full-width': fullWidth,
      }), onDragEnter: this.handleDragEnter, onDragLeave: this.handleDragLeave, onDragOver: this.handleDragOver, onDrop: this.bindHandleDrop }, h("div", { class: cl(`dropzone__container
            dropzone__container--${this.spacingType}`, {
        'dropzone__container--dragging': dragging,
      }) }, h("div", { ref: el => {
        this.slots = el;
      }, class: 'dropzone__slots' }, h("slot", null)))));
  }
  get element() { return this; }
  static get watchers() { return {
    "position": ["handlePosition"]
  }; }
  static get style() { return clDropzoneCss; }
}, [6, "cl-dropzone", {
    "position": [1],
    "fullWidth": [4, "full-width"],
    "fullHeight": [4, "full-height"],
    "hidden": [4],
    "dragging": [32],
    "spacingType": [32]
  }]);
function defineCustomElement$1() {
  if (typeof customElements === "undefined") {
    return;
  }
  const components = ["cl-dropzone"];
  components.forEach(tagName => { switch (tagName) {
    case "cl-dropzone":
      if (!customElements.get(tagName)) {
        customElements.define(tagName, ClDropzone$1);
      }
      break;
  } });
}

const ClDropzone = ClDropzone$1;
const defineCustomElement = defineCustomElement$1;

export { ClDropzone, defineCustomElement };

//# sourceMappingURL=cl-dropzone.js.map